import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  THEMES,
  cssVarsForTheme,
  BOARD_SETS,
  cssVarsForBoardSet,
  type ThemeId,
  type BoardSetId,
} from '@ccc/design-tokens';

export type PieceSetId =
  | 'cburnett'
  | 'merida'
  | 'alpha'
  | 'outline'
  | 'solid'
  | 'carved'
  | 'cyber_neon'
  | 'gold_ebony'
  | 'mythic_fantasy'
  | 'heraldic'
  | 'geometric'
  | 'ornate'
  | 'petal'
  | 'botanical'
  | 'block'
  | 'neon_wire'
  | 'woodcut';

export type PieceSetKind = 'free' | 'supporter';

export interface PieceSetMeta {
  id: PieceSetId;
  name: string;
  blurb: string;
  kind: PieceSetKind;
  /** When true the piece file is a single .png used for the dark side; the
   * lighter side is rendered with a CSS filter invert. When false the set
   * has explicit wK / bK SVG pairs. */
  pngMono?: boolean;
  /** When true the piece is drawn inline via InlinePieces.tsx (theme-aware
   * SVG) rather than loaded from a file. */
  inline?: boolean;
}

export const PIECE_SETS: ReadonlyArray<PieceSetMeta> = [
  // Free — Lichess sets bundled in 1.2
  { id: 'cburnett', name: 'Cburnett', blurb: 'Crisp Lichess classic.', kind: 'free' },
  { id: 'merida', name: 'Merida', blurb: 'Warm tournament style.', kind: 'free' },
  { id: 'alpha', name: 'Alpha', blurb: 'Modern, bold silhouettes.', kind: 'free' },
  // Supporter — original SVG files
  { id: 'outline', name: 'Outline', blurb: 'Clean line-art silhouettes.', kind: 'supporter' },
  { id: 'solid', name: 'Solid', blurb: 'Minimal silhouettes, no detail.', kind: 'supporter' },
  { id: 'carved', name: 'Carved', blurb: 'Subtle radial shading on Staunton shapes.', kind: 'supporter' },
  // Supporter — inline themed SVG (the 8 Claude Design sets)
  { id: 'heraldic', name: 'Heraldic', blurb: 'Traditional Staunton with hand-cut feel.', kind: 'supporter', inline: true },
  { id: 'geometric', name: 'Geometric', blurb: 'Sharp polygons, machined silhouettes.', kind: 'supporter', inline: true },
  { id: 'ornate', name: 'Ornate', blurb: 'Slim columns, filigree dots, varied finials.', kind: 'supporter', inline: true },
  { id: 'petal', name: 'Petal', blurb: 'Soft organic blossoms.', kind: 'supporter', inline: true },
  { id: 'botanical', name: 'Botanical', blurb: 'Each piece a different plant or tree.', kind: 'supporter', inline: true },
  { id: 'block', name: 'Block', blurb: 'Brutalist letter on a hard square.', kind: 'supporter', inline: true },
  { id: 'neon_wire', name: 'Neon Wire', blurb: 'Outline only with electric glow.', kind: 'supporter', inline: true },
  { id: 'woodcut', name: 'Woodcut', blurb: 'Stamp silhouettes with cut detail.', kind: 'supporter', inline: true },
  // Supporter — AI generated PNG
  { id: 'cyber_neon', name: 'Cyber Neon', blurb: 'Glowing neon on dark glass.', kind: 'supporter', pngMono: true },
  { id: 'gold_ebony', name: 'Gold Ebony', blurb: 'Ebony pieces with gold filigree.', kind: 'supporter', pngMono: true },
  { id: 'mythic_fantasy', name: 'Mythic Fantasy', blurb: 'Storybook characters in painterly armor.', kind: 'supporter', pngMono: true },
];

interface ThemeStore {
  themeId: ThemeId;
  pieceSet: PieceSetId;
  /** When non-null AND mixMatch is true, overrides theme board colors. */
  boardSet: BoardSetId | null;
  /** When true the user picks board/pieces independently of theme. */
  mixMatch: boolean;
  setTheme: (id: ThemeId) => void;
  setPieceSet: (id: PieceSetId) => void;
  setBoardSet: (id: BoardSetId | null) => void;
  setMixMatch: (on: boolean) => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      themeId: 'light',
      pieceSet: 'cburnett',
      boardSet: null,
      mixMatch: false,
      setTheme: (id) => {
        set({ themeId: id });
        applyAppearance({ ...get(), themeId: id });
      },
      setPieceSet: (id) => {
        set({ pieceSet: id });
      },
      setBoardSet: (id) => {
        set({ boardSet: id });
        applyAppearance({ ...get(), boardSet: id });
      },
      setMixMatch: (on) => {
        set({ mixMatch: on });
        applyAppearance({ ...get(), mixMatch: on });
      },
    }),
    {
      name: 'ccc-theme',
      partialize: (state) => ({
        themeId: state.themeId,
        pieceSet: state.pieceSet,
        boardSet: state.boardSet,
        mixMatch: state.mixMatch,
      }),
    },
  ),
);

function applyAppearance(state: {
  themeId: ThemeId;
  boardSet: BoardSetId | null;
  mixMatch: boolean;
}): void {
  applyTheme(state.themeId);
  if (state.mixMatch && state.boardSet) {
    const board = BOARD_SETS[state.boardSet];
    if (board) {
      const vars = cssVarsForBoardSet(board);
      const root = document.documentElement;
      for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
    }
  }
}

export function applyTheme(id: ThemeId): void {
  const theme = THEMES[id] ?? THEMES.light;
  if (!theme) return;
  const root = document.documentElement;
  const vars = cssVarsForTheme(theme);
  for (const [k, v] of Object.entries(vars)) {
    root.style.setProperty(k, v);
  }
  root.dataset['scheme'] = theme.scheme;
  root.dataset['theme'] = id;
}

export function initTheme(): void {
  const store = useThemeStore.getState();
  applyAppearance(store);
}
